import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiGet, AuthError } from './api';

/**
 * Which client is the user currently looking at?
 *
 * Every page reads from here rather than holding its own account id. Before
 * this, RulesConfig and CreativeLibrary each hardcoded a single ad account,
 * which meant adding a second client would have required editing and
 * redeploying the frontend.
 *
 * The list of clients comes from the server, derived from the user's grants.
 * The browser never decides what it may see — it only remembers which of the
 * permitted clients was last chosen.
 */

const ClientContext = createContext(null);

const STORAGE_KEY = 'bidsense.selectedClient';

// The platform being worked in, remembered per client: an agency on two
// platforms for one brand and one for another should not have to re-pick.
const platformKey = (clientId) => `bidsense.platform.${clientId}`;

export const PLATFORM_LABEL = { meta: 'Meta', google: 'Google Ads' };

function readPlatform(clientId) {
  try { return localStorage.getItem(platformKey(clientId)); } catch { return null; }
}

export function ClientProvider({ children }) {
  const [clients, setClients] = useState([]);
  const [agencyName, setAgencyName] = useState(null);
  const [role, setRole] = useState(null);

  // Whether the engine is recording decisions or enforcing them. It comes from
  // the server because the browser has no way to know, and because the two
  // places that used to state it were hard-coded strings that said "Observe
  // Mode — nothing is blocked" while the engine was blocking. Defaulting to
  // false means that if this call fails the UI claims enforcement rather than
  // claiming safety, which is the less harmful way to be wrong.
  const [observeMode, setObserveMode] = useState(false);
  const [selectedId, setSelectedId] = useState('ALL');
  const [chosenPlatform, setChosenPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet('/clients')
      .then(data => {
        const list = data.clients || [];
        setClients(list);
        setAgencyName(data.agency_name);
        setRole(data.role);
        setObserveMode(Boolean(data.observe_mode));

        // Restore the previous selection, but only if it is still a client
        // this user may see. Access can be revoked between sessions, and a
        // stale id would otherwise produce a 403 on every page.
        const remembered = localStorage.getItem(STORAGE_KEY);
        const stillPermitted = list.some(c => c.client_id === remembered);

        if (remembered && (remembered === 'ALL' || stillPermitted)) {
          setSelectedId(remembered);
        } else if (list.length === 1) {
          // One client is not a choice, so do not present it as one.
          setSelectedId(list[0].client_id);
        }

        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load your clients.');
        setLoading(false);
      });
  }, []);

  const selectClient = (clientId) => {
    setSelectedId(clientId);
    localStorage.setItem(STORAGE_KEY, clientId);
  };

  const selected = clients.find(c => c.client_id === selectedId) || null;

  // Re-read the remembered platform whenever the client changes.
  useEffect(() => {
    setChosenPlatform(selectedId && selectedId !== 'ALL' ? readPlatform(selectedId) : null);
  }, [selectedId]);

  // Buying and rules are scoped to ONE platform's account. Reporting is not,
  // and does not read this. Meta first when the client has it, because every
  // page predating Google was built against Meta.
  const platformAccounts = selected?.platform_accounts || [];
  const platforms = selected?.platforms || [];
  const platform = platforms.includes(chosenPlatform)
    ? chosenPlatform
    : (platformAccounts[0]?.platform || null);
  const accountFor = (p) =>
    (platformAccounts.find((a) => a.platform === p) || {}).account_id || null;

  const selectPlatform = (p) => {
    if (!platforms.includes(p)) return;
    setChosenPlatform(p);
    try { localStorage.setItem(platformKey(selectedId), p); } catch { /* per-viewer convenience only */ }
  };

  const value = {
    clients,
    agencyName,
    role,
    observeMode,
    loading,
    error,
    selectedId,
    selected,
    selectClient,
    isAll: selectedId === 'ALL',

    // The account of the platform being worked in. Rules and Campaign Build
    // use this. Null when viewing all clients, which pages must handle.
    accountId: selected ? (accountFor(platform) || selected.primary_account_id) : null,

    // Creative is uploaded to Meta, so the creative pages address the client
    // by its Meta account whichever platform the buyer is building on.
    metaAccountId: selected ? (accountFor('meta') || selected.primary_account_id) : null,

    platforms,
    platform,
    selectPlatform,
    // Every connected account with its platform, Meta first - for the
    // platform switcher, which shows the account id under each one.
    platformAccounts,

    // Appended to reporting requests. Empty when viewing everything, so the
    // server falls back to the caller's full scope.
    clientParam: selectedId === 'ALL' ? '' : `?client_id=${selectedId}`,
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
}

export function useClient() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used inside a ClientProvider');
  }
  return context;
}